import graphql__e6aa04f545a0a184d837 from "./__generated__/AnalysisIncomeViewQuery.graphql.ts";import * as React from "react";
import {useTransition} from "react";
import {graphql} from "react-relay";
import {useLazyLoadQuery} from "react-relay/hooks";
import {useIntl} from "react-intl";
import Panel from "./presentational/Panel";
import Hint from "./presentational/Hint";
import KpiCard from "./presentational/KpiCard";
import SegmentedControl from "./presentational/SegmentedControl";
import PeriodTable, {PeriodColumn, PeriodRow} from "./presentational/table/PeriodTable";
import StackedBarChart from "./presentational/chart/StackedBarChart";
import MonthRangeHeader from "./presentational/analysis/MonthRangeHeader";
import MonthKpiCards from "./presentational/analysis/MonthKpiCards";
import RankingList, {RankingEntry} from "./presentational/analysis/RankingList";
import PeriodTableLegend from "./presentational/analysis/PeriodTableLegend";
import {formatCategoryLabel, formatWholeAmount} from "../helper/revenueFormat";
import {monthToDate, useAnalysisMonths} from "../helper/analysisMonths";
import type {AnalysisIncomeViewQuery as AnalysisIncomeViewQueryType} from "./__generated__/AnalysisIncomeViewQuery.graphql";

type ServiceType = '' | 'subscriptions' | 'projects';

const SERVICE_TYPES: readonly ServiceType[] = ['', 'subscriptions', 'projects'];

/**
 * The color of each revenue group in the chart and the table.
 */
const GROUP_COLORS: Record<string, string> = {
    CLUSTERS: '#2563eb', // blue-600
    INSTANCES_AND_PROJECTS: '#10b981', // emerald-500
    DATABASES: '#7c3aed', // violet-600
    STORAGE_AND_TRAFFIC: '#f59e0b', // amber-500
    SERVICES: '#f97316', // orange-500
    OTHER: '#94a3b8', // slate-400
};

/**
 * The revenue over twelve months: net revenue per month of settlement and revenue group,
 * expandable to the cost categories, with the figures and the customer ranking of the selected
 * month. The filters for the type of service and for a single service are kept in the URL.
 */
const AnalysisIncomeView: React.FC = () => {
    const intl = useIntl();
    const [isPending, startPendingTransition] = useTransition();
    const {searchParams, range, selectedMonth, selectMonth, shiftRange, canShiftForward, update} = useAnalysisMonths();

    const typeParameter = searchParams.get('type') ?? '';
    const serviceType: ServiceType = (SERVICE_TYPES as readonly string[]).includes(typeParameter) ? typeParameter as ServiceType : '';
    const serviceIdentifier = searchParams.get('service') ?? '';

    const data = useLazyLoadQuery<AnalysisIncomeViewQueryType>(
        graphql__e6aa04f545a0a184d837,
        {...range, serviceType: serviceType || null, serviceIdentifier: serviceIdentifier || null},
        {fetchPolicy: 'store-and-network'}
    );
    const revenue = data.monthlyRevenue;

    const setFilter = (changes: {type?: ServiceType, service?: string}) => startPendingTransition(() => update({...(changes.type !== undefined && {type: changes.type}), ...(changes.service !== undefined && {service: changes.service})}));
    const select = (month: string) => startPendingTransition(() => selectMonth(month));
    const shift = (delta: number) => startPendingTransition(() => shiftRange(delta));

    const columns: PeriodColumn[] = revenue.months.map(month => ({
        key: month.month,
        label: intl.formatDate(monthToDate(month.month), {month: 'short', year: '2-digit', timeZone: 'UTC'}),
        isProvisional: !month.isComplete,
    }));

    const costCategoryLabel = (identifier: string) => intl.messages[`revenue.costCategory.${identifier}`]
        ? intl.formatMessage({id: `revenue.costCategory.${identifier}`})
        : formatCategoryLabel(identifier);

    const rows: PeriodRow[] = revenue.groups
        .filter(group => group.amounts.some(amount => amount !== 0))
        .map(group => {
            const label = intl.formatMessage({id: `revenue.group.${group.group}`});
            return {
                id: group.group,
                label,
                sparklineLabel: label,
                marker: GROUP_COLORS[group.group],
                values: group.amounts.map(amount => ({amount})),
                total: {amount: group.amount},
                children: group.costCategories.map(costCategory => ({
                    id: costCategory.costCategoryIdentifier,
                    label: costCategoryLabel(costCategory.costCategoryIdentifier),
                    sparklineLabel: costCategoryLabel(costCategory.costCategoryIdentifier),
                    values: costCategory.amounts.map(amount => ({amount})),
                    total: {amount: costCategory.amount},
                })),
            };
        });

    const totalRow: PeriodRow = {
        id: 'total',
        label: intl.formatMessage({id: 'view.analysisIncome.total'}),
        values: revenue.months.map(month => ({amount: month.amount})),
    };

    const selectedIndex = revenue.months.findIndex(month => month.month === selectedMonth);
    const selectedMonthTotal = revenue.months[selectedIndex];
    const hasPreviousMonth = selectedIndex > 0;
    const total = selectedMonthTotal?.amount ?? 0;
    const previousTotal = hasPreviousMonth ? revenue.months[selectedIndex - 1].amount : null;

    const customerRanking: RankingEntry[] = revenue.customers.map(customer => ({
        key: customer.customerAccountIdentifier,
        label: customer.customerName,
        amount: customer.amounts[selectedIndex] ?? 0,
        previousAmount: hasPreviousMonth ? customer.amounts[selectedIndex - 1] : null,
        href: `/customers/${customer.customerAccountIdentifier}`,
    }));
    const customersWithRevenue = customerRanking.filter(customer => customer.amount !== 0).sort((a, b) => b.amount - a.amount);
    const topThreeShare = total > 0 ? customersWithRevenue.slice(0, 3).reduce((sum, customer) => sum + customer.amount, 0) / total : null;
    const newCustomerCount = customerRanking.filter(customer => customer.amount !== 0 && customer.previousAmount === 0).length;
    const lostCustomerCount = customerRanking.filter(customer => customer.amount === 0 && (customer.previousAmount ?? 0) !== 0).length;

    const customerCount = selectedMonthTotal?.customerCount ?? 0;
    const averagePerCustomer = customerCount > 0 ? Math.round(total / customerCount) : 0;

    // The services of the range, narrowed to the selected type, for the picker.
    const services = revenue.services.filter(service =>
        serviceType === '' ||
        (serviceType === 'subscriptions' && service.serviceIdentifier.startsWith('sub-')) ||
        (serviceType === 'projects' && service.serviceIdentifier.startsWith('prj-'))
    );

    return (
        <div className={`space-y-6 px-4 sm:px-6 lg:px-0 transition-opacity ${isPending ? 'opacity-60' : ''}`}>
            <MonthRangeHeader from={range.from} to={range.to} canShiftForward={canShiftForward} onShift={shift}/>

            <Hint>{intl.formatMessage({id: 'view.analysisIncome.notice'})}</Hint>

            <div className="flex flex-wrap items-center gap-3">
                <SegmentedControl
                    size="md"
                    value={serviceType}
                    onChange={value => setFilter({type: value as ServiceType, service: ''})}
                    options={[
                        {value: '', label: intl.formatMessage({id: 'view.analysisIncome.filter.all'})},
                        {value: 'subscriptions', label: intl.formatMessage({id: 'view.analysisIncome.filter.subscriptions'})},
                        {value: 'projects', label: intl.formatMessage({id: 'view.analysisIncome.filter.projects'})},
                    ]}
                />
                <select
                    value={serviceIdentifier}
                    onChange={event => setFilter({service: event.target.value})}
                    className="border border-gray-300 rounded pl-3 pr-8 py-1.5 text-sm w-80 max-w-full"
                >
                    <option value="">{intl.formatMessage({id: 'view.analysisIncome.filter.allServices'})}</option>
                    {services.map(service => (
                        <option key={service.serviceIdentifier} value={service.serviceIdentifier}>{service.label}</option>
                    ))}
                </select>
            </div>

            <MonthKpiCards
                totalLabel={intl.formatMessage({id: 'view.analysisIncome.kpi.total'})}
                selectedMonth={selectedMonth}
                total={total}
                previousTotal={previousTotal}
                averageTotals={revenue.months.filter(month => month.isComplete).map(month => month.amount)}
                formatAmount={formatWholeAmount}
                increaseIsGood={true}
            >
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisIncome.kpi.perCustomer'})}
                    value={formatWholeAmount(averagePerCustomer)}
                    sub={intl.formatMessage({id: 'view.analysisIncome.kpi.perCustomer.sub'}, {customers: customerCount, subscriptions: selectedMonthTotal?.subscriptionCount ?? 0})}
                />
            </MonthKpiCards>

            <Panel>
                <StackedBarChart
                    columns={columns}
                    series={rows.map(row => ({id: row.id, label: String(row.label), color: row.marker ?? '#9ca3af', values: row.values.map(value => value.amount)}))}
                    formatAmount={formatWholeAmount}
                    totalLabel={intl.formatMessage({id: 'view.analysisIncome.total'})}
                    provisionalLegendLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.legend.provisionalShort'})}
                    selectedColumnKey={selectedMonth}
                    onSelectColumn={select}
                />
            </Panel>

            <Panel fullWidthChildren>
                {rows.length === 0
                    ? <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisIncome.empty'})}</p>
                    : <PeriodTable
                        columns={columns}
                        rows={rows}
                        totalRow={totalRow}
                        labelHeader={intl.formatMessage({id: 'view.analysisIncome.col.group'})}
                        totalHeader={intl.formatMessage({id: 'view.analysisIncome.col.total'})}
                        formatAmount={formatWholeAmount}
                        selectedColumnKey={selectedMonth}
                        onSelectColumn={select}
                    />}
                <PeriodTableLegend
                    provisionalLabel={intl.formatMessage({id: 'view.analysisIncome.legend.provisional'})}
                    note={intl.formatMessage({id: 'view.analysisIncome.legend.expand'})}
                />
            </Panel>

            <RankingList
                title={intl.formatMessage({id: 'view.analysisIncome.ranking'})}
                entries={customerRanking}
                formatAmount={formatWholeAmount}
                increaseIsGood={true}
                emptyLabel={intl.formatMessage({id: 'view.analysisIncome.empty'})}
                newLabel={intl.formatMessage({id: 'component.analysis.ranking.new'})}
                goneLabel={intl.formatMessage({id: 'view.analysisIncome.ranking.gone'})}
                barColor="#10b981"
                summary={(
                    <div className="space-y-1">
                        {topThreeShare !== null && <p>{intl.formatMessage({id: 'view.analysisIncome.ranking.topThree'}, {percent: intl.formatNumber(topThreeShare, {style: 'percent', maximumFractionDigits: 0})})}</p>}
                        {hasPreviousMonth && <p>{intl.formatMessage({id: 'view.analysisIncome.ranking.newAndGone'}, {newCount: newCustomerCount, goneCount: lostCustomerCount})}</p>}
                    </div>
                )}
            />
        </div>
    );
};

export default AnalysisIncomeView;
